import { useEffect, useState } from "react";
import { Instagram, Facebook, Youtube, Linkedin, Music2, Send, MessageCircle } from "lucide-react";
import { api, API } from "@/lib/api";

const ICONS = {
  instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin,
  tiktok: Music2, pinterest: Send, whatsapp: MessageCircle, x: Send,
};

const fileUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${API}${u.startsWith("/") ? "" : "/"}${u}`);

export const Footer = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/public/site-settings").then((r) => setS(r.data)).catch(() => setS({}));
  }, []);

  const socials = s?.socials || [];
  const legal = s?.legal_docs || [];
  const partners = s?.partners || [];
  const description = s?.description || "Conseillers en voyages d'exception. Nous concevons des séjours sur-mesure, des escapades urbaines aux retraites les plus confidentielles.";
  const email = s?.contact_email || "contact@dealtravel.eu";
  const note = s?.contact_note || "Réponse sous 24h";

  return (
    <footer data-testid="main-footer" className="border-t border-[#EAE8E3] mt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <h3 className="font-serif-display text-3xl">Deal Travel</h3>
          <p className="mt-4 text-sm text-[#737373] max-w-md leading-relaxed">{description}</p>
          {socials.length > 0 && (
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {socials.map((soc, i) => {
                const Icon = ICONS[(soc.platform || "").toLowerCase()] || Send;
                return (
                  <a key={i} href={soc.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`footer-social-${soc.platform}`} aria-label={soc.platform}
                    className="w-9 h-9 rounded-full border border-[#EAE8E3] flex items-center justify-center text-[#1A1B18] hover:bg-[#1A1B18] hover:text-white transition-colors">
                    <Icon className="h-4 w-4" strokeWidth={1.6} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <p className="label-eyebrow">Contact</p>
          <p className="mt-4 text-sm">{email}</p>
          <p className="mt-1 text-sm text-[#737373]">{note}</p>
        </div>
        <div>
          <p className="label-eyebrow">Maison</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><a href="/#destinations" className="hover:text-[#96836B] transition-colors">Destinations</a></li>
            <li><a href="/carnets" className="hover:text-[#96836B] transition-colors">Carnets de voyage</a></li>
            <li><a href="/#accompagnements" className="hover:text-[#96836B] transition-colors">Nos expériences</a></li>
            <li><a href="/#devis" className="hover:text-[#96836B] transition-colors">Devis sur-mesure</a></li>
          </ul>
        </div>
      </div>

      {partners.length > 0 && (
        <div className="border-t border-[#EAE8E3]" data-testid="footer-partners">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 flex flex-wrap items-center gap-8 justify-center">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">Nos partenaires</span>
            {partners.map((p, i) => {
              const logo = (
                <img src={fileUrl(p.logo_url)} alt={p.name || "Partenaire"} loading="lazy"
                  className="h-8 md:h-9 w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition" />
              );
              return p.url ? (
                <a key={i} href={p.url} target="_blank" rel="noopener noreferrer sponsored"
                  data-testid={`footer-partner-${i}`} aria-label={p.name || "Partenaire"} title={p.name || undefined}>
                  {logo}
                </a>
              ) : (
                <span key={i} data-testid={`footer-partner-${i}`} title={p.name || undefined}>{logo}</span>
              );
            })}
          </div>
        </div>
      )}

      <div className="border-t border-[#EAE8E3]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#737373]">
          <p>© {new Date().getFullYear()} Deal Travel — Tous droits réservés.</p>
          {legal.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-legal">
              {legal.map((d, i) => (
                <a key={i} href={fileUrl(d.url)} target="_blank" rel="noopener noreferrer"
                  data-testid={`footer-legal-${i}`} className="hover:text-[#1A1B18] transition-colors">{d.label}</a>
              ))}
            </div>
          )}
          <p className="tracking-[0.18em] uppercase">Voyages d'exception • Sur-mesure</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
